import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { PERMISSIONS_CACHE_KEY, v2_permissions } from "../actions/v2/rbac";
import constant from "../constant";
import { invalidateCachedGet } from "../utils/apiResponseCache";

const PERM_CACHE_KEY = "vi_permissions_cache";

const PermissionContext = createContext();

const hasAuthSession = () =>
  sessionStorage.getItem(constant.keys.authSession) === "1";

const readPermCache = () => {
  try {
    const raw = sessionStorage.getItem(PERM_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.permissions) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const clearPermissionCache = () => {
  sessionStorage.removeItem(PERM_CACHE_KEY);
  invalidateCachedGet(PERMISSIONS_CACHE_KEY);
};

export const PermissionProvider = ({ children }) => {
  const cached = readPermCache();
  const [permissions, setPermissions] = useState(cached?.permissions || {});
  const [roleName, setRoleName] = useState(cached?.role_name || null);
  const [userRestricted, setUserRestricted] = useState(Boolean(cached?.user_restricted));
  const [accessMode, setAccessMode] = useState(cached?.access_mode || "inherited");
  const [accessAreas, setAccessAreas] = useState(cached?.access_areas || []);
  const [loading, setLoading] = useState(() => hasAuthSession() && !cached);

  const loadPermissions = async ({ background = false } = {}) => {
    if (!hasAuthSession()) {
      setPermissions({});
      setRoleName(null);
      setUserRestricted(false);
      setAccessMode("inherited");
      setAccessAreas([]);
      setLoading(false);
      return;
    }

    if (!background && !readPermCache()) {
      setLoading(true);
    }

    try {
      // Permission revocations must take effect on the next profile load/focus;
      // do not serve a five-minute API cache entry after Access Control changes.
      invalidateCachedGet(PERMISSIONS_CACHE_KEY);
      const res = await v2_permissions();

      if (res.status && res.result) {
        const {
          role_name,
          permissions: perms,
          user_restricted,
          access_mode,
          access_areas,
        } = res.result;
        setRoleName(role_name || null);
        setPermissions(perms || {});
        setUserRestricted(Boolean(user_restricted));
        setAccessMode(access_mode || "inherited");
        setAccessAreas(Array.isArray(access_areas) ? access_areas : []);
        sessionStorage.setItem(
          PERM_CACHE_KEY,
          JSON.stringify({
            role_name,
            permissions: perms || {},
            user_restricted: Boolean(user_restricted),
            access_mode: access_mode || "inherited",
            access_areas: Array.isArray(access_areas) ? access_areas : [],
          })
        );
      } else {
        if (!readPermCache()) {
          setPermissions({});
          setRoleName(null);
          setUserRestricted(false);
          setAccessMode("inherited");
          setAccessAreas([]);
        }
      }
    } catch (err) {
      console.error("Error loading permissions:", err);
      if (!readPermCache()) {
        setPermissions({});
        setRoleName(null);
        setUserRestricted(false);
        setAccessMode("inherited");
        setAccessAreas([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasAuthSession()) {
      setLoading(false);
      return;
    }
    loadPermissions({ background: Boolean(cached) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const refreshOnFocus = () => {
      if (document.visibilityState === "visible" && hasAuthSession()) {
        loadPermissions({ background: true });
      }
    };
    window.addEventListener("focus", refreshOnFocus);
    document.addEventListener("visibilitychange", refreshOnFocus);
    return () => {
      window.removeEventListener("focus", refreshOnFocus);
      document.removeEventListener("visibilitychange", refreshOnFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(
    () => ({
      permissions,
      roleName,
      userRestricted,
      accessMode,
      accessAreas,
      loading,
      reloadPermissions: () => {
        invalidateCachedGet(PERMISSIONS_CACHE_KEY);
        return loadPermissions({ background: false });
      },
    }),
    [permissions, roleName, userRestricted, accessMode, accessAreas, loading]
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => useContext(PermissionContext);
